import { useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, Banknote, CreditCard, Wallet, CheckCircle2, ShoppingBag } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { usePosCharge, PosFeeNote, posFee } from "./usePosCharge";
import ScanBar from "./ScanBar";

const eur = (n) => `€${Number(n || 0).toFixed(2)}`;
const METHODS = [["cash", "Cash", Banknote], ["card", "Card", CreditCard], ["wallet", "Wallet", Wallet]];

export default function DeskSale({ eventId, onBack }) {
  const { authAxios } = useAuth();
  const posRule = usePosCharge();
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [method, setMethod] = useState("cash");
  const [wallet, setWallet] = useState(null);
  const [scanOpen, setScanOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const amt = parseFloat(amount) || 0;

  const findWallet = async (id) => {
    try {
      const r = await authAxios.get(`/wallet/balance/${id.toUpperCase()}`);
      setWallet({ id: r.data.customer.customer_id, name: r.data.customer.name, balance: r.data.wallet?.total_balance });
    } catch (e) { toast.error(e.response?.data?.detail || "Wallet not found"); }
  };

  const sell = async () => {
    if (!(amt > 0)) return toast.error("Enter an amount");
    if (method === "wallet" && !wallet) return toast.error("Scan or enter the buyer's wallet");
    setBusy(true);
    try {
      const r = await authAxios.post(`/accountant/events/${eventId}/desk-sales`, { amount: amt, note, payment_method: method, customer_id: wallet?.id });
      setDone(r.data); toast.success("Sale recorded");
    } catch (e) { toast.error(e.response?.data?.detail || "Sale failed"); }
    finally { setBusy(false); }
  };

  const reset = () => { setDone(null); setAmount(""); setNote(""); setWallet(null); setMethod("cash"); };

  return (
    <div data-testid="desk-sale">
      <button onClick={onBack} className="flex items-center gap-2 text-slate-600 mb-4"><ArrowLeft className="w-5 h-5" /> Back to Event</button>
      <h2 className="font-bold text-lg text-[#1e3a5f] mb-1 flex items-center gap-2"><ShoppingBag className="w-5 h-5" /> Sell Items</h2>
      <p className="text-sm text-slate-500 mb-4">Event desk sale — counted as event income, no merchant payout.</p>
      {done ? (
        <div className="bg-green-50 border border-green-200 rounded-2xl p-5 text-center" data-testid="desk-sale-done">
          <CheckCircle2 className="w-10 h-10 text-green-600 mx-auto mb-2" />
          <p className="font-bold text-green-800">{eur(done.amount)} · {done.payment_method}{done.pos_fee > 0 ? ` + ${eur(done.pos_fee)} payment processing charges` : ""}</p>
          {done.payment_method !== "wallet" && <p className="text-sm text-green-700">Collect {eur(done.buyer_pays)}</p>}
          {done.customer_id && <p className="text-sm text-green-700">Deducted from wallet {done.customer_id}</p>}
          <button onClick={reset} className="mt-4 px-6 py-2.5 rounded-full bg-[#1e3a5f] text-white font-semibold" data-testid="desk-sale-new-btn">New sale</button>
        </div>
      ) : (
        <div className="bg-white rounded-2xl p-5 shadow-sm space-y-4">
          <input type="number" min="0.01" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} placeholder="Amount (€)"
            className="w-full px-4 py-3 rounded-xl border text-2xl font-bold" data-testid="desk-sale-amount-input" />
          <input value={note} onChange={e => setNote(e.target.value)} placeholder="Item / note (optional)" maxLength={200}
            className="w-full px-4 py-2.5 rounded-xl border text-sm" data-testid="desk-sale-note-input" />
          <div className="grid grid-cols-3 gap-2">
            {METHODS.map(([id, label, Icon]) => (
              <button key={id} onClick={() => setMethod(id)} data-testid={`desk-sale-method-${id}`}
                className={`py-3 rounded-xl border-2 font-semibold text-sm flex items-center justify-center gap-1.5 transition-colors ${method === id ? "border-[#1e3a5f] bg-[#1e3a5f]/5 text-[#1e3a5f]" : "border-slate-200 text-slate-500"}`}>
                <Icon className="w-4 h-4" /> {label}
              </button>
            ))}
          </div>
          {method === "card" && <PosFeeNote rule={posRule} amount={amt} testid="desk-sale-pos-fee" />}
          {method === "wallet" && (wallet ? (
            <div className="flex items-center justify-between rounded-xl bg-slate-50 border p-3 text-sm" data-testid="desk-sale-wallet">
              <span><strong>{wallet.name || wallet.id}</strong> · {wallet.id}{wallet.balance != null ? ` · ${eur(wallet.balance)} available` : ""}</span>
              <button onClick={() => setWallet(null)} className="text-xs text-slate-500 underline">Change</button>
            </div>
          ) : <ScanBar onResolve={findWallet} scanOpen={scanOpen} setScanOpen={setScanOpen} />)}
          <button onClick={sell} disabled={busy || !(amt > 0)} className="w-full py-3.5 rounded-full bg-[#1e3a5f] text-white font-bold disabled:opacity-50" data-testid="desk-sale-submit-btn">
            {busy ? "Recording…" : method === "wallet" ? `Charge wallet ${eur(amt)}` : `Collect ${eur(amt + (method === "card" ? posFee(posRule, amt) : 0))} (${method})`}
          </button>
        </div>
      )}
    </div>
  );
}
